import React from "react";
import { translatePlaceholderText } from "../TextContainer/InputPanel/utils";
import { useLanguageContext } from "../LanguageProvider";
import { useTextContainerContext } from "../TextContainer/TextContainerProvider";
import { useEffect, useRef, useState } from "react";
import { Senders } from "../../types";
import { TextContainerProps } from "../TextContainer/TextContainer.types";
import SendButton from "../SendButton";
import EnterSettings from "./EnterSettings";
import useAutosizeTextArea from "./useAutosizeTextArea";
import {
  Container,
  InputDiv,
  TextArea,
  ToolbarContainer,
} from "./StyledTextContainer";
import styled from "styled-components";
import { AIEnhancementTools } from "./AIEnhancementTools";
import { useClientSettings } from "../ClientSettingsProvider/ClientSettingsProvider";
import { Tooltip } from "../Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faClipboard } from "@fortawesome/pro-light-svg-icons";
import IconButton from "../IconButton";
import { ButtonSizes, ButtonVariants } from "../Button/Button.types";
import { useOverlapContext } from "../OverlapProvider/OverlapProvider";
import { OverlapComponentTypes } from "../OverlapProvider/OverlapProvider.types";
import { VerticalDivider } from "./AIEnhancementTools/StyledAIEnhancementTools";

export interface WBBTextContainerProps extends TextContainerProps {
  msgsContainerRef: React.RefObject<HTMLDivElement> | undefined;
  onEnhanceText: (text: string, context: string) => void;
  onSendTemplate: () => void;
  aiValue?: string;
  aiDisabled?: boolean;
  fileUploadEnabed?: boolean;
  loadingElement?: any;
  initialValue?: string;
  draftEventId?: string;
}

const sendOnEnterToken = "send-on-enter";

function WBBTextContainer({
  onSend,
  disabled,
  operatorTyping,
  msgsContainerRef,
  onEnhanceText,
  onSendTemplate,
  aiValue,
  aiDisabled,
  fileUploadEnabed,
  loadingElement,
  initialValue,
  draftEventId,
}: WBBTextContainerProps) {
  const { lang } = useLanguageContext();
  const placeholder = translatePlaceholderText(lang);
  const { textAreaRef, onAttachFile } = useTextContainerContext();
  const { zoomLevel } = useClientSettings();
  const [value, setValue] = React.useState("");
  const [focused, setFocused] = useState(false);
  const [startedTyping, setStartedTyping] = useState(false);
  const [sendOnEnter, setSendOnEnter] = useState(true);
  const ref = useRef<(() => void) | never>();
  const timerRef = useRef<null | ReturnType<typeof setTimeout>>(null);
  const { onOpen } = useOverlapContext();

  const resetAutoSize = useAutosizeTextArea(
    textAreaRef,
    msgsContainerRef,
    value,
    timerRef,
    zoomLevel,
    false
  );

  useEffect(() => {
    const sendOnEnterInit = window.localStorage.getItem(sendOnEnterToken);
    setSendOnEnter(sendOnEnterInit ? sendOnEnterInit === "true" : true);
  }, []);

  const onFocus = () => setFocused(true);
  const onBlur = () => setFocused(false);

  function onCustomerTyping() {
    operatorTyping(Senders.USER);
  }

  useEffect(() => {
    ref.current = onCustomerTyping;
  }, [onCustomerTyping]);

  const handlePaste = (e?: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!e || !fileUploadEnabed) {
      textAreaRef?.current?.focus();
      return;
    }
    const { items } = e.clipboardData;
    let isFile = false;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        onAttachFile(file!, null);
        isFile = true;
      }
    }
    if (isFile) {
      e.preventDefault();
    }
    return;
  };

  useEffect(() => {
    if (aiValue) {
      setValue(aiValue);
      handlePaste();
    }
  }, [aiValue]);

  useEffect(() => {
    setValue(initialValue || "");
    handlePaste();
  }, [initialValue]);

  const debouncedCallback = () => {
    const func = () => {
      ref.current?.();
    };
    if (!startedTyping) {
      func();
      setStartedTyping(true);
      setTimeout(() => {
        setStartedTyping(false);
      }, 3000);
    }
  };

  function handleChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    event.preventDefault();
    debouncedCallback();
    setValue(event.currentTarget.value);
  }

  function sendHandler() {
    if (!value?.length) return;
    onSend(value, draftEventId);
    setValue("");
    resetAutoSize();
  }
  function handleSend(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    event.stopPropagation();
    sendHandler();
  }

  function handleKeyDown(event: any) {
    const isEnter = event.key === "Enter";
    const ctrlEnter = isEnter && event.ctrlKey;
    const sendWhenEnter = sendOnEnter && isEnter && !event.shiftKey;
    if (sendWhenEnter || ctrlEnter) {
      event.preventDefault();
      sendHandler();
    }
  }

  function handleChangeCheckbox() {
    const token = window.localStorage.getItem(sendOnEnterToken);
    window.localStorage.setItem(
      sendOnEnterToken,
      token ? `${token !== "true"}` : "false"
    );
    setSendOnEnter((prev) => !prev);
  }

  function handleSendTemplate(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    event.preventDefault();
    onSendTemplate();
  }

  function handleEnhancement(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    event.preventDefault();
    onEnhanceText(value, event.currentTarget.id);
    if (timerRef.current) clearTimeout(timerRef.current);
  }

  function uploadImage() {
    onOpen({
      componentType: OverlapComponentTypes.UploadFile,
      props: { onAttachFile },
    });
  }

  return (
    <Container>
      <InputDiv focused={focused}>
        <TextArea
          rows={1}
          disabled={disabled}
          ref={textAreaRef}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          onPaste={handlePaste}
        />
        <EnterSettings
          checked={sendOnEnter}
          onChange={handleChangeCheckbox}
          disabled={disabled}
        />
        <ToolbarContainer>
          <ToolsContainer>
            <AIEnhancementTools
              disabled={disabled || !value?.length || aiDisabled}
              onEnhanceText={handleEnhancement}
            />
            <VerticalDivider />
            <TemplateContainer>
              <Tooltip content="Send Template Response">
                <IconButton
                  variant={ButtonVariants.text}
                  size={ButtonSizes.small}
                  onClick={handleSendTemplate}
                  disabled={disabled}
                >
                  <FontAwesomeIcon fixedWidth={true} icon={faClipboard} />
                </IconButton>
              </Tooltip>
            </TemplateContainer>
            {fileUploadEnabed && (
              <>
                <VerticalDivider />
                <>
                  <Tooltip content="Upload an image">
                    <IconButton
                      variant={ButtonVariants.text}
                      size={ButtonSizes.small}
                      disabled={false}
                      id="friendly"
                      onClick={uploadImage}
                    >
                      <FontAwesomeIcon icon={faImage} />
                    </IconButton>
                  </Tooltip>
                </>
              </>
            )}
          </ToolsContainer>
          {loadingElement ? (
            loadingElement
          ) : (
            <SendButton
              onClick={handleSend}
              isPortal={true}
              disabled={disabled}
            />
          )}
        </ToolbarContainer>
      </InputDiv>
    </Container>
  );
}

export default WBBTextContainer;

const TemplateContainer = styled.div<{ disabled?: boolean }>`
  & > div {
    & > button {
      & > svg {
        color: ${({ theme }) => theme.colors.secondary.mainContrast};
        height: 14px;
        width: auto;
        opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
      }
      &:is(:hover, :focus-visible, :active) {
        & > svg {
          color: ${({ theme }) => theme.colors.primary.main};
        }
      }
    }
  }
`;

const ToolsContainer = styled.div`
  display: flex;
  gap: 5px;
`;
